import { AWSIoTProvider } from '@aws-amplify/pubsub/lib/Providers';
import { BusinessObjectAssembler, isValidEdgeType } from '@iotv/datamodel';
import { GatewayClassName, isMinimalUserObject, QueryTypes, } from '@iotv/datamodel-core';
import { ComparisonType, isValidBusinessObject, PathDirection, } from '@iotv/iotv-v3-types';
import { Amplify, Hub, PubSub } from 'aws-amplify';
import { v4 } from 'uuid';
import config from '../../config';
import AmplifyIoTListener from '../../listeners/amplifyIoTListener';
import { UserFunctions } from '../../redux/actions/AppActionFunctions/AppActions';
import { AdjacentType, LogicalOperatorType, } from '../../types/AppTypes';
import { getCognitoId } from '../aws/DebugAuth';
import ApiGateway from '../aws/api-gateway/ApiGateway';
import { getObject } from '../aws/s3/UserBlobs';
const { REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
const dyn = config.aws.dynamoDB;
/**
 * Moved from top-level index.tsx to fix chunking / bundling issues
 * https://github.com/aws-amplify/amplify-js/issues/10829#issuecomment-1512409749
 */
if (Amplify.PubSub._pluggables < 1)
    Amplify.addPluggable(new AWSIoTProvider({
        aws_pubsub_region: config.aws.default_region,
        aws_pubsub_endpoint: `wss://${config.aws.iot.endpointAddress}/mqtt`,
    }));
Hub.listen('pubsub', (data) => {
    const { payload } = data;
    debug && console.log(payload);
});
const displayMessage = (content, type) => {
    UserFunctions.setUserMessage({ id: v4(), content, label: content, type });
};
const assembler = new BusinessObjectAssembler();
const iotListener = AmplifyIoTListener;
/**
 * Saves the {@link zombie} to the datastore
 *
 * @param zombie object to be saved
 * @returns promise of the saved object
 */
export async function save(zombie) {
    debug && console.log('DAO TRANSACTION: pre get intance for object', JSON.stringify(zombie));
    const businessObject = assembler.getInstance(zombie);
    debug && console.log('DAO TRANSACTION: getInstance', assembler.getInstance(zombie), businessObject?.getUserAttributes());
    debug && console.log('DAO TRANSACTION: saving object', JSON.stringify(businessObject));
    const path = '/save/' + businessObject?.type;
    return await ApiGateway.post(path, businessObject);
}
const SystemManagementAPIOnlyTypes = [GatewayClassName];
/**
 * Deletes the {@link zombie}, **and** any ascendent / descendent edges
 *
 * @remarks
 *
 * Only works with full objects, i.e. will not run if provided with an edge (`pk !== sk`) — use {@link deleteExact | `deleteExact()`} instead!
 *
 * @param zombie object to be deleted
 * @returns promise of the deleted object
 */
export async function deleteObject(zombie) {
    if (SystemManagementAPIOnlyTypes.includes(zombie.type)) {
        const message = `${zombie.type} may only be deleted via the System Management API!`;
        console.error(`deleteObject: ${message}`);
        return { err: new Error(message), data: null };
    }
    const businessObject = assembler.getInstance(zombie);
    debug && console.log('DAO TRANSACTION: deleting object', JSON.stringify(businessObject));
    const path = '/delete/' + businessObject?.type;
    return await ApiGateway.post(path, businessObject);
}
/**
 * Deletes the {@link zombie}, even if it is an edge, **without** removing any ascendent / descendent edges
 *
 * @param zombie object to be deleted
 * @returns promise of the deleted object
 */
export async function deleteExact(zombie) {
    if (zombie.pk === zombie.sk && SystemManagementAPIOnlyTypes.includes(zombie.type)) {
        const message = `${zombie.type} may only be deleted via system management api`;
        console.log(`deleteExact: ${message}`);
        return { err: new Error(message), data: null };
    }
    console.log('DAO TRANSACTION: deleting exact object', JSON.stringify(zombie));
    const path = '/deleteExact';
    const res = await ApiGateway.post(path, zombie);
    console.log('delete exact res', res);
    return res;
}
/**
 * Creates an edge from {@link zombieA} (source) to {@link zombieB} (target), and returns the source and edge items.
 *
 * @remarks
 *
 * ```
 * [zombieA] --> (edge) --> [zombieB]
 * ```
 *
 * In Gremlin terms, {@link zombieA} will always be `outV`, {@link zombieB} will always be `inV`
 *
 * @param zombieA the object that will be the source of the edge
 * @param zombieB the object that will be the target of the edge
 * @param edgeTypeId optional `typeId` for the edge
 * @param edgeAttributes optional attributes for the edge
 * @returns promise of a {@link LinkObjectResponse}
 */
export async function link(zombieA, zombieB, edgeTypeId, edgeAttributes) {
    debug && console.log(`DAO TRANSACTION: linking objects ${zombieA?.name}, ${zombieB?.name} with edgeTypeId ${edgeTypeId}`, { zombieA, zombieB });
    const businessObjectA = assembler.getInstance(zombieA);
    const businessObjectB = assembler.getInstance(zombieB);
    const { isValid, reason, resolvedEdgeTypeId } = isValidEdgeType({ sourceObjectTypeId: zombieA.type, targetObjectTypeId: zombieB.type, edgeTypeId });
    if (isValid && (edgeTypeId || resolvedEdgeTypeId)) {
        const path = '/link/' + businessObjectA?.type + '/' + businessObjectB?.type;
        if (isValidBusinessObject(businessObjectA) && isValidBusinessObject(businessObjectB)) {
            const body = {
                objectA: businessObjectA,
                objectB: businessObjectB,
            };
            if (edgeTypeId ?? resolvedEdgeTypeId) {
                body.edgeTypeId = edgeTypeId ?? resolvedEdgeTypeId;
                if (edgeAttributes)
                    body.edgeAttributes = edgeAttributes;
            }
            return await ApiGateway.post(path, body);
        }
        else {
            console.log('WARN bad zombie to instance result', { zombieA, businessObjectA, zombieB, businessObjectB });
            return { err: { message: `not valid input`, zombieA, zombieB }, data: null };
        }
    }
    else {
        const message = `Invalid edge with edgeType ${edgeTypeId} (${reason}), resolved edge type id: ${resolvedEdgeTypeId}`;
        console.log(message);
        return { err: { message, zombieA, zombieB }, data: null };
    }
}
/**
 * Removes an edge from {@link zombieA} (source) to {@link zombieB} (target), and returns the source and edge items.
 *
 * @remarks
 *
 * ```
 * [zombieA] --> (edge) --> [zombieB]
 * ```
 *
 * In Gremlin terms, {@link zombieA} will always be `outV`, {@link zombieB} will always be `inV`
 *
 * @param zombieA object that is the source of the edge
 * @param zombieB object that is the target of the edge
 * @param edgeTypeId (optional) for the edge
 * @param edgeAttributes (optional) attributes for the edge
 * @returns promise of a {@link UnlinkResponse}
 */
export async function unlink(zombieA, zombieB, edgeTypeId, edgeAttributes) {
    const businessObjectA = assembler.getInstance(zombieA);
    const businessObjectB = assembler.getInstance(zombieB);
    debug && console.log(`DAO TRANSACTION: unlinking objects ${businessObjectA?.name}, ${businessObjectB?.name}`, { businessObjectA, businessObjectB });
    const path = '/unlink/' + businessObjectA?.type + '/' + businessObjectB?.type;
    if (isValidBusinessObject(businessObjectA) && isValidBusinessObject(businessObjectB)) {
        const body = {
            objectA: businessObjectA,
            objectB: businessObjectB,
        };
        if (edgeTypeId) {
            body.edgeTypeId = edgeTypeId;
            if (edgeAttributes)
                body.edgeAttributes = edgeAttributes;
        }
        const unlinkRes = await ApiGateway.post(path, body);
        debug && console.log('DAO Unlink Res', unlinkRes);
        return unlinkRes;
    }
    else
        return { err: { message: 'not valid input', zombieA, zombieB, businessObjectA, businessObjectB }, data: null };
}
export const createAndLink = async ({ existingObject, newObject, edgeTypeId, edgeAttributes, direction = PathDirection.child, }) => {
    const [parentObject, childObject] = direction === PathDirection.child ? [existingObject, newObject] : [newObject, existingObject];
    let res = { err: null, data: null };
    if (childObject && parentObject) {
        const saveRes = await save(newObject);
        if (saveRes.data) {
            const linkRes = await link(parentObject, childObject, edgeTypeId, edgeAttributes);
            if (linkRes.err) {
                debug && console.log('link err', linkRes);
                displayMessage(linkRes.err.message, 'MessageBar');
            }
            else if (linkRes.data instanceof Array) {
                debug && console.log('link res', linkRes);
                displayMessage(`Linked ${childObject.eui} to ${parentObject?.name ?? parentObject.type}`, 'SnackBar');
            }
            res = linkRes;
        }
        else if (saveRes.err) {
            debug && console.log('saveRes.err', saveRes.err);
            displayMessage(saveRes.err.message, 'MessageBar');
            res.err = { message: saveRes.err.message, zombieA: parentObject, zombieB: childObject };
        }
    }
    return res;
};
/**
 * Searches from the {@link contextObject} for objects of {@link typeId}
 *
 * @remarks
 *
 * Limited to 40 items per iteration, call again with the {@link lastEvaluatedKey} to resume a search
 *
 * @param contextObject to search from
 * @param includeContextLinkedItems ???
 * @param typeId to search for
 * @param lastEvaluatedKey
 * @returns promise of a {@link SearchByTypeResponse}
 */
export async function searchByType(contextObject, includeContextLinkedItems, typeId, lastEvaluatedKey) {
    debug && console.log(`DAO TRANSACTION: searching for type ${typeId} with lastEvaluated key ${lastEvaluatedKey}`);
    const path = '/query/';
    const body = {
        type: QueryTypes.searchByType,
        contextObject,
        includeContextLinkedItems,
        items: [{ type: typeId }],
        limit: 40,
        scanForward: true,
        exclusiveStartKey: lastEvaluatedKey,
    };
    debug && console.log('Search By Type query', JSON.stringify(body, null, 1));
    const response = (await ApiGateway.post(path, body));
    debug && console.log('search by type res', response);
    return response;
}
/**
 * Fetches objects adjacent to {@link object}, either parents or children
 *
 * @param object to fetch adjacent from
 * @param type to filter adjacent objects by
 * @param direction `PathDirection.PARENT` or `PathDirection.CHILD`
 * @param edgeTypeId optional `typeId` to filter edges
 * @returns promise of a {@link GetAdjacentResponse}
 */
export async function getAdjacent(object, type, direction, edgeTypeId) {
    debug && console.log('DAO TRANSACTION: Get Adjacent', { object, type, direction });
    const res = { err: null, data: null };
    const adjacencyType = direction === PathDirection.parent ? AdjacentType.PARENT : AdjacentType.CHILD;
    const params = {
        contextObjects: [object],
        path: [{ objectTypeId: type, adjacencyType, edgeTypeId }],
    };
    const viaGraphRes = await getGraphQueryResponse(params);
    try {
        if (viaGraphRes.data) {
            res.data = viaGraphRes.data.lastNodesAndEdges.nodes;
        }
        else {
            if (res.err || res.data === undefined) {
                console.log(`err in getAdjacent with params`, params);
            }
            res.err = viaGraphRes.err;
        }
    }
    catch (e) {
        console.log(`err in getAdjacent with parmas`, { params, dataOut: viaGraphRes.data });
        throw e;
    }
    debug && console.log('get adjacent result', res);
    return res;
}
/**
 * Fetches objects adjacent to a scope-defining context object, either parents or children
 *
 * @remarks
 *
 * Modern version of {@link getAdjacent} with more configurability
 *
 * @param getAdjacentRequest the scope-defining context object, adjacency type, and object type to fetch
 * @returns promise of a {@link QueryCommandOutputNoMeta}
 */
export const getAdjacent2Wrapper = async (getAdjacentRequest) => {
    const { excludeRelatedToObject, scopeDefinitingContextObject, adjacencyType, objectTypeId, ExclusiveStartKey, limit, edgeTypeId, includeEdges } = getAdjacentRequest;
    return await getAdjacent2(excludeRelatedToObject, scopeDefinitingContextObject, adjacencyType, objectTypeId, ExclusiveStartKey, limit, edgeTypeId, includeEdges);
};
/** Unused in LSC UI, perhaps use {@link getAdjacent2Wrapper} instead? */
export const getAdjacent2 = async (excludeRelatedToObject, scopeDefinitingContextObject, adjacencyType, objectTypeId, ExclusiveStartKey = undefined, limit = 200, edgeTypeId, includeEdges) => {
    debug && console.log('DAO TRANSACTION: getAdjacents 2', { pageContextObject: excludeRelatedToObject, objectTypeId, scopeDefinitingContextObject });
    const queryBody = {
        queryType: QueryTypes.LIST_LIMITED_ALL_TYPES,
        contextObject: scopeDefinitingContextObject,
        objectTypeId,
        adjacencyType,
        includeNodes: true,
        includeEdges,
        ExclusiveStartKey,
        limit,
        excludeRelatedToObject,
        filterGroups: [],
    };
    if (edgeTypeId) {
        queryBody.filterGroups?.push({
            filters: [{ key: 'edgeTypeId', value: edgeTypeId, predicate: ComparisonType.EQUALS }],
            setOperation: LogicalOperatorType.AND,
        });
    }
    const res = simpleAdjacentQuery(queryBody);
    return res;
};
/**
 * Fetches one {@link item} from the datastore
 *
 * @param item to fetch
 * @returns a promise of a {@link ValidBusinessObject}
 */
export const getOne = async (item) => {
    const path = '/getOne';
    const res = (await ApiGateway.post(path, item));
    return res;
};
/**
 * Fetches one {@link vob | Datastore Object Type} from the datastore
 *
 * @param vob to fetch
 * @returns a promise of a {@link QueryCommandOutputNoMeta}
 */
export const getExact = async (vob) => {
    const apiRef = 'ConsumerApi';
    const query = {
        TableName: dyn.tableName,
        IndexName: dyn.priName,
        ExpressionAttributeNames: {
            '#pk': dyn.pKey,
            '#sk': dyn.sKey,
        },
        ExpressionAttributeValues: {
            ':pk': vob.pk,
            ':sk': vob.sk,
        },
        KeyConditionExpression: '#pk = :pk and #sk = :sk',
    };
    const path = '/baseQuery/';
    const res = await ApiGateway.post(path, query, apiRef);
    return res;
};
/** when refactoring this is a copy of AppDAO getMainTableQueryResultsWithReturn */
export async function baseQuery(query) {
    const apiRef = 'ConsumerApi';
    const path = '/baseQuery/';
    const response = await ApiGateway.post(path, query, apiRef);
    return response;
}
export async function getApexInstances(objectTypeId) {
    const path = '/getApexInstance';
    const res = (await ApiGateway.post(path, { type: objectTypeId }));
    return res;
}
export async function getApexGroupsOfSupportedType(supportedType) {
    const path = '/getApexGroupsOfSupportedType';
    const res = (await ApiGateway.post(path, { supportedType }));
    return res;
}
export const getGraphQueryResponse = async (request) => {
    const path = '/graphQuery';
    const responseBody = (await ApiGateway.post(path, request));
    return responseBody;
};
export const getParents = async (contextObject, objectTypeId, ExclusiveStartKey = undefined, limit = 200, edgeTypeId) => {
    debug && console.log('DAO TRANSACTION: getParents', { contextObject, objectTypeId });
    // const queryBody: TraverserQueryPathBody = {
    //   type: QueryTypes.simplePath,
    //   simpleTypePath: [ {type, direction: PathDirection.parent}],
    //   items: [user],
    //   normalize: false
    // }
    const queryBody = {
        queryType: QueryTypes.LIST_LIMITED_ALL_TYPES,
        contextObject,
        objectTypeId,
        adjacencyType: AdjacentType.PARENT,
        includeNodes: true,
        includeEdges: true,
        ExclusiveStartKey,
        limit,
        excludeRelatedToObject: undefined,
        filterGroups: [],
    };
    if (edgeTypeId) {
        queryBody.filterGroups?.push({
            filters: [{ key: 'edgeTypeId', value: edgeTypeId, predicate: ComparisonType.EQUALS }],
            setOperation: LogicalOperatorType.AND,
        });
    }
    // const path = '/query/';
    // //debug && console.log('History Query', queryBody);
    // const res = await ApiGateway.post(path, queryBody);
    // debug && console.log('RES FOR GET PARENTS', res);
    // const parentsAndEdges = res?.data;
    // debug && console.log('Parents of type', parentsAndEdges);
    const res = simpleAdjacentQuery(queryBody);
    return res;
};
/** Matches signature of ConsumerAPI method for simpleAdjacency query and exposes additional parameters to original getAdjacents */
export const simpleAdjacentQuery = async (queryBody) => {
    const path = '/query/';
    //debug && console.log('History Query', queryBody);
    const res = await ApiGateway.post(path, queryBody);
    debug && console.log('simpleAdjacentQuery result', res);
    const parentsAndEdges = res?.data;
    debug && console.log('Parents of type', parentsAndEdges);
    return res;
};
export async function getThingShadowStates(things, nodeEdgeMapper) {
    debug && console.log('DAO TRANSACTION: getThingShadowStates');
    const latestShadowStateZombies = things.map((thing) => {
        return { pk: thing.sk, sk: thing.sk + '_latestShadowState' };
    });
    debug && console.log('latestShadowStateZombies', latestShadowStateZombies);
    await getMultipleObjectsFromZombies(latestShadowStateZombies, nodeEdgeMapper);
}
export async function getThingAlerts(things, nodeEdgeMapper) {
    debug && console.log('DAO TRANSACTION: getThingAlerts');
    const latestAlertZombies = things.map((thing) => {
        return { pk: thing.sk, sk: thing.sk + '_latestAlert' };
    });
    debug && console.log('latestAlertZombies', latestAlertZombies);
    await getMultipleObjectsFromZombies(latestAlertZombies, nodeEdgeMapper);
}
export async function batchGet(zombies) {
    debug && console.log(`DAO TRANSACTION: batchGet ( is array ${zombies instanceof Array})`);
    const path = '/batchGet';
    const res = await ApiGateway.post(path, zombies);
    debug && console.log('batchGet res', { zombies, res });
    const items = res.data;
    return items;
}
export async function getMultipleObjectsFromZombies(zombies, nodeEdgeMapper) {
    debug && console.log('DAO TRANSACTION: getMultipleObjectsFromZombies');
    const items = await batchGet(zombies);
    debug && console.log('Items', items);
    if (nodeEdgeMapper && items) {
        nodeEdgeMapper.loadItems(items);
    }
}
const requestPolicyAttach = async (error = undefined, user) => {
    const IdentityId = await getCognitoId();
    console.log('requestPolicyAttach', { user, IdentityId });
    debug && console.log('DAO TRANSACTION: requestPolicyAttach');
    error && console.log(error);
    if (user && isMinimalUserObject(user) && IdentityId) {
        debug && console.log('Attempting to trigger request policy attach ', IdentityId);
        user.IdentityId = IdentityId;
        const res = await save(user);
        debug && console.log(res);
    }
};
export async function scan(scan) {
    const apiRef = 'ConsumerApi';
    const path = '/scan/';
    const response = await ApiGateway.post(path, scan, apiRef);
    return response;
}
export async function ioStatus() {
    const apiRef = 'ConsumerApi';
    const path = '/io/status/';
    const response = await ApiGateway.post(path, {}, apiRef);
    return response;
}
export async function ioImport(job) {
    const apiRef = 'ConsumerApi';
    const path = '/io/import/';
    const response = await ApiGateway.post(path, job, apiRef);
    return response;
}
export async function ioExport(job) {
    const apiRef = 'ConsumerApi';
    const path = '/io/export/';
    const response = await ApiGateway.post(path, job, apiRef);
    return response;
}
export const subscribeToIOQueue = (callback) => {
    const topic = config.aws.mqtt.ioQueueTopic;
    debug && console.log(`DAO TRANSACTION: subscribeToIOQueue ${topic}`);
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to', topic);
    return PubSub.subscribe(topic).subscribe({
        next: callback,
        error: (error) => console.log('MQTT import/export queue error', error),
        complete: () => console.log(`subscribeToIOQueue unsubscribed from ${topic}`),
    });
};
export const subscribeToIOJob = (user, callback) => {
    const topic = config.aws.mqtt.ioJobTopic.replace(/\[.*?\]/, user.id);
    debug && console.log(`DAO TRANSACTION: subscribeToIOJob ${topic}`);
    if (!user.IdentityId) {
        console.warn('subscribeToIOJob requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            debug && console.log('subscribeToCustomerThingTopic finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to', topic);
    return PubSub.subscribe(topic).subscribe({
        next: callback,
        error: (error) => console.log('MQTT import/export job error', error),
        complete: () => console.log(`subscribeToIOJob unsubscribed from ${topic}`),
    });
};
/**
 * [RSE]: 2024 11 extended with optional customer as default is to send on customer channel to avoid max subscrptions limit
 */
export const subscribeToCustomerThingTopic = ({ customer, thing, user, callback }) => {
    const topic = config.aws.mqtt.customerEntityStateUpdate.replace('[customerSk]', customer.sk).replace(`[entitySk]`, thing?.sk ?? '#');
    debug && console.log(`DAO TRANSACTION: subscribeToCustomerThingTopic ${topic}`);
    if (!user.IdentityId) {
        console.warn('subscribeToCustomerThingTopic requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            console.log('subscribeToCustomerThingTopic finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to ', topic);
    const observable = PubSub.subscribe(topic);
    const subscription = observable.subscribe({
        next: (data) => iotListener.callBackComponent(data, callback),
        error: (error) => requestPolicyAttach(error, user),
        complete: () => console.log(`subscribeToCustomerThingTopic unsubscribed from ${topic}`),
    });
    return subscription;
};
export const subscribeToThingTopic = (thing, user) => {
    debug && console.log('DAO TRANSACTION: subscribeToThingTopic');
    const topic = config.aws.mqtt.appDeviceMessageConfirmedTopice.replace('thingId', thing.id);
    if (!user.IdentityId) {
        console.warn('subscribeToThingTopic requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            debug && console.log('subscribeToCustomerThingTopic finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to ', topic);
    return PubSub.subscribe(topic).subscribe({
        next: (data) => iotListener.updateFromMessage(data),
        error: (error) => requestPolicyAttach(error, user),
        complete: () => console.log(`subscribeToThingTopic unsubscribed from ${topic}`),
    });
};
export const subscribeToNetworkChannel = (user, callback) => {
    debug && console.log('DAO TRANSACTION: subscribeToNetworkChannel');
    const topic = config.aws.mqtt.networkChannel.replace('[app_id]', '#');
    if (!user.IdentityId) {
        console.warn('subscribeToNetworkChannel requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            debug && console.log('subscribeToCustomerThingTopic finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to ', topic);
    return PubSub.subscribe(topic).subscribe({
        next: (data) => iotListener.callBackComponent(data, callback),
        error: (error) => requestPolicyAttach(error, user),
        complete: () => console.log(`subscribeToNetworkChannel unsubscribed from ${topic}`),
    });
};
/**
 * Subscribes to the provided topic
 *
 * Current suggested practice usage is to
 * - call this function via a `useEffect()` based on what needs to be subcribed to
 * - hold the returned subscription in a `useRef()`,
 * - use the return of the `useEffect()` to cancel the subscription
 */
export const subscribeToMQTTChannel = (user, topic, callback) => {
    debug && console.log(`DAO TRANSACTION subscribeToMQTTChannel: ${topic}`);
    if (!user.IdentityId) {
        console.warn('subscribeToMQTTChannel requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            debug && console.log('subscribeToMQTTChannel finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    try {
        debug && console.log('App subscribing subscribeToMQTTChannel to', topic);
        const observable = PubSub.subscribe(topic);
        const subscription = observable.subscribe({
            next: (data) => iotListener.callBackComponent(data, callback),
            error: (error) => requestPolicyAttach(error, user),
            complete: () => console.log(`subscribeToMQTTChannel unsubscribed from ${topic}`),
        });
        return subscription;
    }
    catch (e) {
        console.log('Err subscribeToMQTTChannel caught', e.message);
    }
};
export const subscribeToThingAlertTopic = (thing, user) => {
    debug && console.log('DAO TRANSACTION: subscribeToThingAlertTopic');
    const topic = config.aws.mqtt.alertTopic.replace('thingId', thing.id);
    if (!user.IdentityId) {
        console.warn('subscribeToThingAlertTopic requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            debug && console.log('subscribeToCustomerThingTopic finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to ', topic);
    return PubSub.subscribe(topic).subscribe({
        next: (data) => iotListener.updateFromMessage(data),
        error: (error) => requestPolicyAttach(error, user),
        complete: () => console.log(`subscribeToThingAlertTopic unsubscribed from ${topic}`),
    });
};
export const subscribeToUserChannel = (user) => {
    debug && console.log('DAO TRANSACTION: subscribeToUserChannel');
    const topic = config.aws.mqtt.userChannel.replace('[userId]', user.id);
    if (!user.IdentityId) {
        console.warn('subscribeToUserChannel requesting policy as no IdentityId on user');
        requestPolicyAttach(undefined, user).then(() => {
            debug && console.log('subscribeToCustomerThingTopic finished requesting policy');
        });
    }
    if (process.env.REACT_APP_NO_MQTT)
        return undefined;
    debug && console.log('App subscribing to ', topic);
    return PubSub.subscribe(topic).subscribe({
        next: (data) => iotListener.updateFromMessage(data.value),
        error: (error) => requestPolicyAttach(error, user),
        complete: () => console.log(`subscribeToUserChannel unsubscribed from ${topic}`),
    });
};
export async function getThingImageUrl(thing) {
    debug && console.log('DAO TRANSACTION: getThingImageUrl');
    const imageKey = thing.name + '.jpeg';
    if (imageKey) {
        const presignedUrl = await getObject(imageKey);
        debug && console.log('URL,', presignedUrl);
        thing.presignedUrl = presignedUrl;
    }
}
export async function getObjectHistory({ vob, objectHistoryFilter }) {
    const path = '/getObjectHistory';
    const res = (await ApiGateway.post(path, { sk: vob.sk, objectHistoryFilter }, 'ConsumerApi'));
    return res;
}
export async function saveObjectHistory({ sk, objectHistory }) {
    const path = '/saveObjectHistory';
    const res = (await ApiGateway.post(path, { sk, objectHistory }, 'ConsumerApi'));
    return res;
}
